import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Calendar, Plane, Users, AlertCircle, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import Mandala from "@/components/Mandala";
import SignatureExperiences from "@/components/SignatureExperiences";
import ShareButton from "@/components/ShareButton";
import SEO from "@/components/SEO";

const INCLUSIONS = [
  "Six days / five nights of retreat experience",
  "Five nights of mountain accommodations",
  "Daily access to a resident herd of horses",
  "Two guided two-hour trail rides",
  "Sacred equine connection sessions",
  "Shamanic drumming journey with intuitive painting",
  "Meditation and gentle movement practices",
  "Visit to Mount Princeton Hot Springs & Spa",
  "Scenic hike to Paradise Cove with picnic",
  "Sauna and hot tub sessions",
  "Soap making workshop",
  "Cowgirl hat creation experience",
  "All meals (excluding 1 lunch in Buena Vista) prepared by a private chef",
  "Evening fires and stargazing",
  "Round-trip transportation from Colorado Springs Airport",
  "Lots of memorable photos!",
];

const ACCOMMODATIONS = [
  { name: "The Farmhouse",   desc: "2-bedroom cabin with full bath and kitchen.",                                                                  status: "Sold out", soldOut: true },
  { name: "The Glass House", desc: "1-bedroom cabin sharing a bath and kitchen with the Farmhouse.",                                              status: "Available", soldOut: false },
  { name: "The Herd Homes",  desc: "4 tiny homes — 1 queen private room and 1 shared room with 2 single beds, each with full bath and kitchen.", status: "1 left",   soldOut: false, urgent: true },
];

const NOT_INCLUDED = [
  "Flights",
  "Gratuities (recommended for transportation & trail guides)",
  "Lunch in Buena Vista while visiting Mount Princeton Hot Springs",
  "Spa services at Mount Princeton Hot Springs",
  "Extra embellishments at the Hat Bar",
];

// Fallbacks for image slots when admin hasn't uploaded yet — uses Holly's existing artifacts.
const FALLBACKS = {
  "return-portrait":  "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/yipigdji_Holly%202014%20Passions%20Production%20Co-40.jpeg",
  "rhythm-1":         "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/rpeocub0_SEDONARETREAT-131.png",
  "copper-lodge":     "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/uvz6fcvb_IMG_8274.jpeg",
  "rhythm-2":         "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/f2nrwkzu_Page89option.jpg",
  // Dead WP-hosted URLs replaced with working Emergent artifacts. Holly can
  // override via Admin → Page Images → Solstice.
  "nourishment":      "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/f2nrwkzu_Page89option.jpg",
  "holly":            "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/nk1vssa4_SEDONARETREAT-52.jpg",
};

export default function SolsticeRetreat() {
  const [offering, setOffering] = useState(null);
  const [images, setImages] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [offRes, imgRes] = await Promise.all([
          api.get("/offerings/solstice-in-the-saddle-a-sacred-mountain-retreat-for-women"),
          api.get("/page-images/solstice"),
        ]);
        setOffering(offRes.data);
        setImages(imgRes.data?.slots || {});
      } catch {}
    })();
  }, []);

  const img = (key) => images[key]?.[0] || FALLBACKS[key];
  const accomGallery = images["accommodations-gallery"] || [];

  return (
    <div className="bg-aviana-paper" data-testid="solstice-page">
      <SEO
        title="Solstice in the Saddle · A Sacred Mountain Retreat for Women"
        description="A 6-day women's healing retreat in the Colorado Rockies — June 18-23, 2026. Horseback trail rides, Mount Princeton hot springs, sound healing, sister circles, and embodied yoga. Limited to 12 sisters. Includes lodging, meals, and shuttle."
        path="/retreats/solstice-in-the-saddle"
        image="/social/og-solstice.jpg"
        keywords="women's retreat Colorado, equine retreat, horseback yoga retreat, Mount Princeton hot springs retreat, summer solstice retreat, sister circle, sacred feminine retreat, women's healing retreat 2026"
        type="event"
        jsonLd={offering ? {
          "@context": "https://schema.org",
          "@type": "Event",
          "name": "Solstice in the Saddle · A Sacred Mountain Retreat for Women",
          "description": "A 6-day women's healing retreat in the Colorado Rockies — horseback trail rides, Mount Princeton hot springs, sound healing, sister circles, and embodied yoga.",
          "startDate": offering.start_date,
          "endDate": offering.end_date,
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": {
            "@type": "Place",
            "name": "The Copper Lodge · Buena Vista, Colorado",
            "address": {"@type": "PostalAddress", "addressLocality": "Buena Vista", "addressRegion": "CO", "addressCountry": "US"}
          },
          "image": offering.image_url,
          "organizer": {"@id": "https://avianayoga.com/#organization"},
          "performer": {"@id": "https://avianayoga.com/#holly"},
          "offers": {
            "@type": "Offer",
            "price": offering.price_usd,
            "priceCurrency": "USD",
            "url": "https://avianayoga.com/retreats/solstice-in-the-saddle",
            "availability": offering.spots_left > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut"
          }
        } : null}
      />
      {/* ─── Hero ─── */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-aviana-plumDeep via-aviana-plumDeep/90 to-aviana-ink">
          <img src={img("hero")} alt="" className="w-full h-full object-cover opacity-40 mix-blend-overlay" />
        </div>
        <Mandala size={500} variant="rose" className="absolute -top-24 -right-32 text-aviana-goldSoft/15 hidden md:block" />
        <div className="container-aviana relative z-10 pt-28 pb-20 text-aviana-cream">
          <p className="overline text-aviana-goldSoft mb-5" data-testid="hero-overline">A Sacred Mountain Retreat for Women</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.98] max-w-4xl">
            Solstice<br />in the <span className="italic text-aviana-goldSoft">Saddle</span>
          </h1>
          <div className="mt-9 flex flex-wrap gap-x-9 gap-y-3 text-aviana-cream/85 text-[14px]">
            <span className="flex items-center gap-2"><Calendar size={16} className="text-aviana-goldSoft" /> June 18 – 23, 2026</span>
            <span className="flex items-center gap-2"><MapPin size={16} className="text-aviana-goldSoft" /> Guffey, Colorado · The Copper Lodge</span>
            <span className="flex items-center gap-2"><Users size={16} className="text-aviana-goldSoft" /> 6 days · 5 nights · Limited to small group</span>
          </div>
          <p className="mt-8 text-aviana-cream/85 text-[18px] md:text-[20px] leading-[1.55] max-w-2xl font-serif italic">
            Discovering sanctuary in the mountains where the land, the horses, and your own breath guide you home.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a href="#reserve" className="btn-gold" data-testid="hero-cta-reserve">Reserve your spot <ArrowRight size={14} /></a>
            <a href="#imagine" className="text-aviana-goldSoft hover:text-aviana-cream text-[13px] tracking-[0.18em] uppercase" data-testid="hero-cta-learn">
              ↓ See what awaits you
            </a>
            <ShareButton
              title="Solstice in the Saddle · A Sacred Mountain Retreat for Women"
              slug="solstice-in-the-saddle"
              kind="retreat"
              label="Send to a sister"
            />
          </div>
          {typeof offering?.spots_left === "number" && offering.spots_left <= 5 && (
            <p className="mt-8 inline-flex items-center gap-2 bg-aviana-gold/15 border border-aviana-goldSoft/30 rounded-full px-4 py-2 text-aviana-goldSoft text-[12px] tracking-[0.14em] uppercase" data-testid="spots-banner">
              <AlertCircle size={14} /> Only {offering.spots_left} {offering.spots_left === 1 ? "spot" : "spots"} left
            </p>
          )}
        </div>
      </section>

      {/* ─── A Return to What is Real (oval image, like homepage) ─── */}
      <section className="py-28" id="imagine">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 lg:gap-20 items-center max-w-6xl">
          <div className="lg:col-span-5 lg:order-2">
            <OvalImage src={img("return-portrait")} alt="A return to what is real" aspect="3/4" />
          </div>
          <div className="lg:col-span-7 lg:order-1">
            <p className="overline-plum mb-4">An invitation</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05] mb-8">A return to what is <em className="italic gold-text">real.</em></h2>
            <div className="space-y-5 text-aviana-inkSoft text-[16px] leading-[1.8]">
              <p>In a world that asks you to be constantly available, productive, and connected, it is easy to forget what it feels like to simply <em>be</em>. To listen to your body. To feel the ground beneath your feet. To remember that you, too, are part of nature.</p>
              <p>This retreat is an invitation to step out of the buzzing pace of everyday life and into the deep quiet of the mountains — where ancient peaks hold steady, valleys breathe slowly, and time seems to soften.</p>
              <p>Here, in a remote mountain sanctuary, you are welcomed into spaciousness. <strong className="text-aviana-plum">To wander, to breathe, to enjoy and be in joy. Where you can feel held by the land.</strong></p>
              <p className="font-serif italic text-aviana-plum text-[18px] leading-[1.5]">Most of all, you will be given a rare and precious gift: <em>time</em>.</p>
              <p className="font-display text-aviana-ink text-2xl pt-2">You are welcome here.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Cinematic rhythm photo 1 — full-bleed horses ─── */}
      <CinematicBand src={img("rhythm-1")} caption="The medicine of horses — beings who live in presence, honesty, and connection." />

      {/* ─── A Day at the Copper Lodge — now with image ─── */}
      <section className="py-24 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <Mandala size={380} variant="lotus" className="absolute -bottom-12 -right-16 text-aviana-goldSoft/10 hidden md:block" />
        <div className="container-aviana relative grid lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-6xl">
          <div className="lg:col-span-5">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] ring-1 ring-aviana-goldSoft/30 shadow-2xl">
              <img src={img("copper-lodge")} alt="The Copper Lodge" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-aviana-plumDeep/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-[10px] tracking-[0.22em] uppercase text-aviana-goldSoft">The Copper Lodge</p>
                <p className="font-display text-2xl text-aviana-cream mt-1">Guffey, Colorado</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline text-aviana-goldSoft mb-4">Imagine</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-cream leading-[1.05]">A day at the<br /><em className="italic text-aviana-goldSoft">Copper Lodge.</em></h2>
            <div className="mt-8 space-y-5 text-aviana-cream/85 text-[15px] leading-[1.8]">
              <p>Imagine waking to mountain stillness, sunlight pouring across the valley, and the quiet presence of horses grazing nearby.</p>
              <p>Mornings begin slowly — meditation, gentle movement, or a cup of coffee enjoyed in silence as the land wakes around you. Some days lead you into the saddle for expansive trail rides through mountain terrain. Other moments invite you into sacred connection with the herd, observing, listening, and allowing the horses to mirror what's ready to be seen.</p>
              <p>Afternoons might carry you to Paradise Cove for a scenic hike and picnic, followed by a refreshing plunge into glacial waters. Or they may be spent soaking at Mount Princeton Hot Springs, letting mineral-rich waters soften your body and quiet your mind.</p>
              <p>Evenings gather us around the fire — stories, laughter, music, and spontaneous movement beneath wide, star-filled skies.</p>
              <p className="font-serif italic text-aviana-goldSoft text-[16px]">Each day holds a gentle rhythm, guided yet unhurried, allowing you to feel grounded, inspired, and free.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Inclusions ─── */}
      <section className="py-24">
        <div className="container-aviana">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="overline-plum mb-3">Your investment includes</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink">Everything held with <em className="italic gold-text">care.</em></h2>
          </div>
          <ul className="grid md:grid-cols-2 gap-x-12 gap-y-3 max-w-4xl mx-auto" data-testid="inclusions-list">
            {INCLUSIONS.map((line) => (
              <li key={line} className="flex gap-3 py-2 border-b border-aviana-border/60">
                <Check size={16} className="text-aviana-gold shrink-0 mt-1" />
                <span className="text-aviana-inkSoft text-[15px] leading-[1.6]">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─── Signature Experiences — the soulful selling points ─── */}
      <SignatureExperiences variant="solstice" />

      {/* ─── Cinematic rhythm photo 2 — hot springs/mountains ─── */}
      <CinematicBand src={img("rhythm-2")} caption="Soaking in mineral hot springs beneath open skies." />

      {/* ─── Nourishment — now with oval image ─── */}
      <section className="py-28 bg-aviana-cream/40">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 lg:gap-20 items-center max-w-6xl">
          <div className="lg:col-span-5">
            <OvalImage src={img("nourishment")} alt="Chef-prepared meals" aspect="4/5" />
          </div>
          <div className="lg:col-span-7">
            <p className="overline-plum mb-3">The nourishment</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">Meals that <em className="italic gold-text">hold you.</em></h2>
            <div className="mt-7 space-y-5 text-aviana-inkSoft text-[15px] leading-[1.8]">
              <p>The cornerstone of every Aviana retreat — fresh, seasonal, thoughtfully prepared meals designed to energize your body and soothe your nervous system.</p>
              <p><strong className="text-aviana-plum">All meals (excluding one lunch in Buena Vista) are prepared by our private chef</strong>, using locally sourced and high-quality ingredients. Mornings include nourishing breakfast options that let you begin your day slowly and intuitively.</p>
              <p className="italic text-aviana-muted text-[13.5px]">Special dietary needs can be honored and accommodated with advance notice.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Accommodations — slideshow + 3 cabin cards ─── */}
      <section className="py-24">
        <div className="container-aviana">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="overline-plum mb-3">The accommodations</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink">Mountain cabins, chosen with <em className="italic gold-text">care.</em></h2>
            <p className="mt-5 text-aviana-inkSoft leading-[1.75] text-[15px]">Comfortable, thoughtfully chosen mountain accommodations that support rest, privacy, and connection — free from light pollution and unnecessary noise. Each cabin accommodates up to three women, every guest in her own bed.</p>
          </div>

          <AccommodationSlideshow photos={accomGallery} />

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-12" data-testid="accommodations">
            {ACCOMMODATIONS.map((a) => (
              <div key={a.name} className={`bg-aviana-cream rounded-2xl border p-7 ${a.urgent ? "border-aviana-gold" : a.soldOut ? "border-aviana-border opacity-60" : "border-aviana-border"}`} data-testid={`accom-${a.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <h3 className="font-display text-2xl text-aviana-ink">{a.name}</h3>
                <p className="mt-2 text-aviana-inkSoft text-[14px] leading-[1.65]">{a.desc}</p>
                <p className={`mt-5 text-[11px] tracking-[0.18em] uppercase font-semibold ${a.soldOut ? "text-aviana-muted" : a.urgent ? "text-red-700" : "text-green-700"}`}>
                  {a.status}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-aviana-muted text-[13px] italic max-w-xl mx-auto">Assigned on a first-come, first-served basis and thoughtfully arranged to create a harmonious, supportive living environment.</p>
        </div>
      </section>

      {/* ─── Investment + Reserve ─── */}
      <section id="reserve" className="py-24 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <Mandala size={520} variant="star" className="absolute -top-20 left-1/2 -translate-x-1/2 text-aviana-goldSoft/10" />
        <div className="container-aviana relative max-w-3xl text-center">
          <p className="overline text-aviana-goldSoft mb-3">The investment</p>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-cream">Reserve your spot.</h2>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <div className="bg-aviana-plumDeep/60 border border-aviana-gold/30 rounded-2xl p-8 text-left">
              <p className="overline text-aviana-goldSoft text-[10px] mb-2">Individual</p>
              <p className="font-figure text-5xl text-aviana-cream">$2,940</p>
              <p className="text-aviana-cream/70 text-[13px] mt-2">per person</p>
            </div>
            <div className="bg-aviana-goldSoft/10 border border-aviana-goldSoft rounded-2xl p-8 text-left">
              <p className="overline text-aviana-goldSoft text-[10px] mb-2">Sisterhood savings</p>
              <p className="font-figure text-5xl text-aviana-goldSoft">$2,740</p>
              <p className="text-aviana-cream/70 text-[13px] mt-2">per person · register with 2 friends · save $200 each</p>
            </div>
          </div>

          <div className="mt-10 bg-aviana-plumDeep/40 border border-aviana-gold/30 rounded-2xl p-8 text-left">
            <p className="font-display text-2xl text-aviana-cream mb-4">A <span className="text-aviana-goldSoft"><span className="font-figure text-[0.85em]">$500</span> non-refundable deposit</span> secures your place.</p>
            <ul className="space-y-3 text-aviana-cream/85 text-[14px]">
              <li className="flex gap-3"><Check size={16} className="text-aviana-goldSoft shrink-0 mt-0.5" /> Remaining balance paid in full by <strong>June 3rd</strong>, or</li>
              <li className="flex gap-3"><Check size={16} className="text-aviana-goldSoft shrink-0 mt-0.5" /> Split into <strong>5 monthly payments</strong> leading up to the retreat</li>
              <li className="flex gap-3"><Check size={16} className="text-aviana-goldSoft shrink-0 mt-0.5" /> Payment plans configured at checkout</li>
            </ul>
          </div>

          <p className="mt-10 text-aviana-cream/70 italic font-serif text-[15px] max-w-xl mx-auto">
            This retreat is intentionally limited in size to preserve depth, safety, and connection. Your investment reflects the personalized care, premium accommodations, professional facilitation, and sacred container that supports your transformation.
          </p>

          <Link to="/offering/solstice-in-the-saddle-a-sacred-mountain-retreat-for-women" className="btn-gold mt-10" data-testid="reserve-cta">
            Reserve with $500 deposit <ArrowRight size={14} />
          </Link>
          {typeof offering?.spots_left === "number" && (
            <p className="mt-6 text-aviana-goldSoft text-[12px] tracking-[0.18em] uppercase">{offering.spots_left} {offering.spots_left === 1 ? "spot" : "spots"} remaining</p>
          )}
        </div>
      </section>

      {/* ─── Meet Holly (oval portrait) ─── */}
      <section className="py-24">
        <div className="container-aviana grid lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-5xl">
          <div className="lg:col-span-5">
            <OvalImage src={img("holly")} alt="Holly Johnson" aspect="3/4" />
          </div>
          <div className="lg:col-span-7">
            <p className="overline-plum mb-3">Your facilitator</p>
            <h2 className="font-display text-4xl text-aviana-ink">Holly Johnson</h2>
            <p className="mt-1 text-aviana-gold italic">International yoga teacher trainer · author · retreat specialist</p>
            <div className="mt-6 space-y-4 text-aviana-inkSoft leading-[1.75]">
              <p>Holly blossoms from an extensive background of research, study, and direct experience of therapeutic avenues to balance and mend the relationships between mind, body, and Spirit.</p>
              <p>A former professional in the horse industry, she attributes much of her personal growth and spiritual understanding to the medicine of the horse. She is certified as a nutritional therapist, personal trainer, equine massage therapist, aromatherapist, Reiki master, Thai bodyworker, and 200-hour yoga instructor.</p>
              <p>Her extensive comprehension of optimal alignment, spiritual understanding, and energetic sensitivity lends to her authentic presence as a heartfelt, humbled leader.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Getting There + Not Included ─── */}
      <section className="py-20 bg-aviana-cream/40">
        <div className="container-aviana grid md:grid-cols-2 gap-10 max-w-5xl">
          <div>
            <p className="overline-plum mb-3 flex items-center gap-2"><Plane size={14} /> Getting there</p>
            <h3 className="font-display text-2xl text-aviana-ink mb-4">The Copper Lodge · Guffey, CO</h3>
            <p className="text-aviana-inkSoft leading-[1.75] text-[15px]">10591 Co Rd 102, Guffey, CO 80820</p>
            <div className="mt-5 space-y-3 text-aviana-inkSoft leading-[1.75] text-[15px]">
              <p><strong>Colorado Springs Airport (recommended):</strong> Group transport leaves at <strong>4:30 pm on Thursday June 18th</strong>. Plan to arrive by 4:15 pm.</p>
              <p><strong>Denver International Airport:</strong> Rent a car, or arrange transport to Colorado Springs Airport by 4:15 pm.</p>
              <p><strong>Returning home:</strong> Shuttle departs the Copper Lodge at <strong>10 am on June 23rd</strong>, arriving by 11:30 am to Colorado Springs Airport.</p>
              <p className="text-[13px] text-aviana-muted italic mt-3 pt-3 border-t border-aviana-border/60"><strong className="not-italic text-aviana-ink">Missed the shuttle?</strong> An Uber/Lyft from Colorado Springs Airport to the Copper Lodge runs approximately <strong className="not-italic text-aviana-ink">$115 each way</strong>.</p>
            </div>
          </div>
          <div>
            <p className="overline-plum mb-3">Not included</p>
            <h3 className="font-display text-2xl text-aviana-ink mb-4">A few details to budget for.</h3>
            <ul className="space-y-2 text-aviana-inkSoft text-[15px]">
              {NOT_INCLUDED.map((l) => (
                <li key={l} className="flex gap-3"><span className="text-aviana-muted/40 mt-1">•</span> {l}</li>
              ))}
            </ul>
            <p className="mt-6 text-[12px] text-aviana-muted italic">Travel insurance is strongly recommended. TravelGuard offers comprehensive plans including <em>"Cancel for Any Reason"</em> coverage through their Gold Package.</p>
          </div>
        </div>
      </section>

      {/* ─── Cancellation policy ─── */}
      <section className="py-20">
        <div className="container-aviana max-w-3xl">
          <p className="overline-plum mb-3">Cancellation policy</p>
          <h3 className="font-display text-2xl text-aviana-ink mb-6">Designed with care, anchored in reality.</h3>
          <div className="space-y-4 text-aviana-inkSoft text-[14px] leading-[1.75]">
            <p>We understand life can be unpredictable. We've designed this policy with care and compassion — and at the same time, hosting an intimate retreat requires significant advance planning and non-refundable expenses.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              <div className="bg-aviana-cream rounded-xl border border-aviana-border p-5">
                <p className="overline text-aviana-gold text-[10px] mb-2">60+ days before</p>
                <p className="text-[13px]">Refund of all payments beyond the $500 deposit. Travel costs non-refundable.</p>
              </div>
              <div className="bg-aviana-cream rounded-xl border border-aviana-border p-5">
                <p className="overline text-aviana-gold text-[10px] mb-2">30–59 days before</p>
                <p className="text-[13px]">50% credit toward another Aviana retreat within 12 months. Remaining balance forfeited.</p>
              </div>
              <div className="bg-aviana-cream rounded-xl border border-aviana-border p-5">
                <p className="overline text-aviana-gold text-[10px] mb-2">0–29 days before</p>
                <p className="text-[13px]">No refunds or credits. All balances paid in full by June 3rd.</p>
              </div>
            </div>
            <p className="mt-6 italic text-aviana-muted">Full cancellation policy and details on travel insurance available — reach out to <a href="mailto:avianayoga@gmail.com" className="text-aviana-plum hover:text-aviana-gold underline">avianayoga@gmail.com</a> with any questions.</p>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-24 bg-aviana-ink text-aviana-cream text-center">
        <div className="container-aviana max-w-2xl">
          <Mandala size={80} variant="lotus" className="text-aviana-goldSoft opacity-60 mx-auto mb-7" />
          <h2 className="font-display text-4xl md:text-5xl">Step into the fullness of yourself.</h2>
          <p className="mt-5 text-aviana-cream/80 leading-[1.75]">Rooted in the earth. Lifted by the sun. Supported by sisterhood. The summer solstice is waiting.</p>
          <Link to="/offering/solstice-in-the-saddle-a-sacred-mountain-retreat-for-women" className="btn-gold mt-10" data-testid="final-cta">
            I'm ready to find my bliss <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}

/* ───── Oval image — homepage-style gold halo + 50% radius ───── */
function OvalImage({ src, alt, aspect = "3/4" }) {
  const aspectClass = aspect === "4/5" ? "aspect-[4/5]" : "aspect-[3/4]";
  return (
    <div className="relative" data-testid="oval-image">
      <div className="absolute -inset-4 rounded-[50%] bg-gradient-to-br from-aviana-goldSoft via-aviana-gold to-aviana-goldDeep opacity-25 blur-2xl" />
      <div
        className={`relative overflow-hidden ring-[3px] ring-aviana-gold/40 ${aspectClass}`}
        style={{ borderRadius: "50%" }}
      >
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      </div>
      <Mandala size={64} variant="lotus" className="absolute -top-3 -right-3 text-aviana-gold/70" />
    </div>
  );
}

/* ───── Cinematic full-bleed image band between sections ───── */
function CinematicBand({ src, caption }) {
  return (
    <section className="relative h-[58vh] md:h-[65vh] overflow-hidden">
      <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover scale-105" />
      <div className="absolute inset-0 bg-gradient-to-b from-aviana-ink/30 via-transparent to-aviana-ink/60" />
      {caption && (
        <div className="absolute bottom-10 left-0 right-0 px-6 text-center">
          <p className="font-serif italic text-aviana-cream text-[16px] md:text-[22px] leading-[1.4] max-w-2xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
            {caption}
          </p>
        </div>
      )}
    </section>
  );
}

/* ───── Accommodations slideshow ───── */
function AccommodationSlideshow({ photos }) {
  const [idx, setIdx] = useState(0);
  const hasPhotos = photos && photos.length > 0;

  if (!hasPhotos) {
    return (
      <div className="max-w-4xl mx-auto bg-aviana-cream/60 border border-dashed border-aviana-border rounded-[2rem] aspect-[16/9] flex flex-col items-center justify-center text-center p-10" data-testid="accom-slideshow-empty">
        <ImageIcon size={28} className="text-aviana-plum/40 mb-3" />
        <p className="overline-plum mb-1">Photos coming soon</p>
        <p className="text-aviana-muted text-[13px] max-w-md">Holly is curating the cabin photos. Admin → Page Images → Solstice → "Accommodations slideshow" to add up to 8.</p>
      </div>
    );
  }

  const total = photos.length;
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  return (
    <div className="max-w-5xl mx-auto" data-testid="accom-slideshow">
      <div className="relative rounded-[2rem] overflow-hidden aspect-[16/9] bg-aviana-plum/10 shadow-xl ring-1 ring-aviana-border">
        <img src={photos[idx]} alt={`Accommodation photo ${idx + 1}`} className="w-full h-full object-cover transition-opacity duration-500" key={idx} />
        <div className="absolute inset-0 bg-gradient-to-t from-aviana-ink/40 via-transparent to-transparent pointer-events-none" />
        {total > 1 && (
          <>
            <button onClick={prev} aria-label="Previous" className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-aviana-cream/90 backdrop-blur-sm border border-aviana-border text-aviana-ink hover:bg-aviana-cream transition-colors flex items-center justify-center" data-testid="accom-prev">
              <ChevronLeft size={18} />
            </button>
            <button onClick={next} aria-label="Next" className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-aviana-cream/90 backdrop-blur-sm border border-aviana-border text-aviana-ink hover:bg-aviana-cream transition-colors flex items-center justify-center" data-testid="accom-next">
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-aviana-ink/40 backdrop-blur-sm rounded-full px-3 py-2">
              {photos.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-aviana-cream" : "w-1.5 bg-aviana-cream/50 hover:bg-aviana-cream/80"}`} data-testid={`accom-dot-${i}`} />
              ))}
            </div>
          </>
        )}
      </div>
      {total > 1 && (
        <p className="text-center text-[11px] tracking-[0.22em] uppercase text-aviana-muted mt-4">{idx + 1} of {total}</p>
      )}
    </div>
  );
}
